import { useEffect, useMemo, useRef, useState } from "react";
import { Gauge, Timer, Coffee, Flag, PartyPopper, Undo2, BedDouble, X, Loader2, Zap, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { haversineKm as distKm, buildRouteInfo, kmOnRoute, dayWindow } from "../lib/geo";
import { socAt, buildSocPoints } from "./SocChart";

const BatteryCoach = ({ trip, day, kmDone, movedKm, onRefresh }) => {
  const dismissKey = `vr-battcoach-${trip.id}-${day.day_number}-${new Date().toISOString().slice(0, 10)}`;
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(dismissKey));
  const [busy, setBusy] = useState(false);

  const buffer = trip.charge_buffer_pct || 15;
  const firstDriveNum = (trip.days || []).find((d) => d.type !== "stay" && (d.distance_km || 0) > 0)?.day_number;
  const startSoc = day.start_soc_override ?? (day.day_number === firstDriveNum ? (trip.start_battery_pct || 100) : 100);
  const extraKm = Math.max((movedKm || 0) - (kmDone || 0), 0);
  const factor = (day.weather || {}).range_factor || 1;
  const usable = (trip.vehicle?.range_km || 300) * 0.85 * factor;
  const drain = (extraKm / usable) * 100;
  const pts = buildSocPoints(trip, day, startSoc);
  const socNow = socAt(pts, kmDone || 0) - drain;
  const nextStop = (day.charging_stops || [])
    .filter((c) => c.km_from_day_start != null && c.km_from_day_start > (kmDone || 0))
    .sort((a, b) => a.km_from_day_start - b.km_from_day_start)[0];
  const projected = (nextStop ? (nextStop.arrival_soc ?? buffer) : (day.arrival_soc ?? 0)) - drain;

  if (dismissed || extraKm < 15 || projected >= buffer || socNow <= 0) return null;

  const dismiss = () => { localStorage.setItem(dismissKey, "1"); setDismissed(true); };
  const replan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${day.day_number}/replan`, {
        current_soc: Math.max(Math.round(socNow), 1),
        km_done_in_day: Math.round(kmDone || 0),
      });
      toast.success(`Laadplan herrekend: ${data.charging_stops.length} laadstop${data.charging_stops.length === 1 ? "" : "s"} resterend, aankomst ~${data.arrival_soc}%`);
      dismiss();
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="battery-coach-banner" className="mt-4 rounded-2xl border border-red-400/40 bg-red-500/10 p-4">
      <div className="flex items-start gap-3">
        <img src="/volt-avatar.png" alt="Volt" className="w-9 h-9 rounded-full shrink-0 border-2 border-red-400/50" />
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-snug">
            <span className="font-bold text-red-400">Je bent ~{Math.round(extraKm)} km omgereden.</span>{" "}
            {nextStop ? `Bij je volgende laadstop kom je naar schatting op ~${Math.max(Math.round(projected), 0)}% uit` : `Je komt naar schatting aan met ~${Math.max(Math.round(projected), 0)}%`} — onder je buffer van {buffer}%. Zal ik je laadplan herrekenen vanaf je huidige positie (nu ~{Math.max(Math.round(socNow), 0)}%)?
          </p>
          <div className="flex gap-2 mt-3">
            <button data-testid="battery-coach-replan" onClick={replan} disabled={busy}
              className="rounded-full bg-red-400 hover:bg-red-300 text-[#0F172A] text-xs font-bold px-4 py-2 flex items-center gap-1.5 disabled:opacity-50 active:scale-95 transition-[transform,background-color] duration-200">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Herbereken laadstops
            </button>
            <button data-testid="battery-coach-dismiss" onClick={dismiss}
              className="rounded-full bg-secondary hover:bg-border text-xs font-semibold px-4 py-2 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
              <X className="w-3.5 h-3.5" /> Nee, laat maar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DelayCoach = ({ trip, day, deltaMin, kmDone, remainingKm, onRefresh }) => {
  const dismissKey = `vr-delaycoach-${trip.id}-${day.day_number}-${new Date().toISOString().slice(0, 10)}`;
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(dismissKey));
  const [open, setOpen] = useState(false);
  const maxAhead = Math.max(Math.round(remainingKm) - 20, 21);
  const [aheadKm, setAheadKm] = useState(() => Math.min(60, Math.max(Math.round(remainingKm / 2 / 5) * 5, 20)));
  const [busy, setBusy] = useState(false);
  if (dismissed) return null;

  const dismiss = () => { localStorage.setItem(dismissKey, "1"); setDismissed(true); };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${day.day_number}/split`, { km: Math.round((kmDone || 0) + aheadKm) });
      toast.success(`Overnachting rond ${data.location} ingepland — je planning wordt opnieuw berekend`);
      dismiss();
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="delay-coach-banner" className="mt-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4">
      <div className="flex items-start gap-3">
        <img src="/volt-avatar.png" alt="Volt" className="w-9 h-9 rounded-full shrink-0 border-2 border-amber-500/50" />
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-snug">
            <span className="font-bold text-amber-500">Je bent ~{fmtDur(deltaMin)} achter op schema.</span> Zal ik een overnachting onderweg inplannen? Dan splitsen we de rit van vandaag in twee dagen.
          </p>
          {!open ? (
            <div className="flex gap-2 mt-3">
              <button data-testid="delay-coach-plan-button" onClick={() => setOpen(true)}
                className="rounded-full bg-amber-500 hover:bg-amber-400 text-[#0F172A] text-xs font-bold px-4 py-2 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
                <BedDouble className="w-3.5 h-3.5" /> Plan overnachting
              </button>
              <button data-testid="delay-coach-dismiss" onClick={dismiss}
                className="rounded-full bg-secondary hover:bg-border text-xs font-semibold px-4 py-2 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
                <X className="w-3.5 h-3.5" /> Nee, ik rij door
              </button>
            </div>
          ) : (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span>Overnachting na nog</span>
                <span className="font-mono font-bold text-amber-500">{aheadKm} km rijden</span>
              </div>
              <input data-testid="delay-coach-slider" type="range" min="20" max={maxAhead} step="5" value={Math.min(aheadKm, maxAhead)}
                onChange={(e) => setAheadKm(parseInt(e.target.value))} className="w-full accent-amber-500 mt-1" />
              <p className="text-[11px] text-muted-foreground font-mono">nog ~{Math.max(Math.round(remainingKm) - aheadKm, 0)} km voor morgen · trip wordt 1 dag langer</p>
              <button data-testid="delay-coach-apply" onClick={apply} disabled={busy}
                className="w-full mt-2 rounded-full bg-amber-500 hover:bg-amber-400 text-[#0F172A] text-xs font-bold py-2.5 flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95 transition-[transform,background-color] duration-200">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BedDouble className="w-3.5 h-3.5" />} Splits & herbereken trip
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtDur = (min) => {
  if (min == null || !isFinite(min)) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
};

export const LiveProgress = ({ trip, day, pos, fallbackPos, onKmDone, onRefresh }) => {
  const arrived = !!day?.arrived_at;
  const storageKey = `vr-live-${trip.id}-${day?.day_number}-${new Date().toISOString().slice(0, 10)}`;
  const [stats, setStats] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || { driveSec: 0, stopSec: 0, movedKm: 0 }; }
    catch { return { driveSec: 0, stopSec: 0, movedKm: 0 }; }
  });
  const lastRef = useRef(null);
  const [kmDone, setKmDone] = useState(null);
  const [etaInfoOpen, setEtaInfoOpen] = useState(false);
  useEffect(() => { setKmDone(null); lastRef.current = null; }, [day?.day_number]);

  const routeInfo = useMemo(() => buildRouteInfo(trip.route), [trip.route]);

  useEffect(() => {
    const effPos = pos || fallbackPos;
    let km = null;
    if (effPos && routeInfo && day?.route_start_km != null) {
      const onRoute = kmOnRoute(routeInfo, effPos, 5, dayWindow(day));
      if (onRoute != null) {
        km = Math.min(Math.max(onRoute - day.route_start_km, 0), day.distance_km);
        setKmDone(km);
      }
    }
    if (pos) {
      const departed = (km ?? kmDone ?? 0) >= 0.5;
      const now = Date.now();
      const last = lastRef.current;
      lastRef.current = { ...pos, t: now };
      if (last && departed) {
        const dt = Math.min((now - last.t) / 1000, 120);
        const d = distKm(last, pos);
        const speed = d / (dt / 3600);
        setStats((s) => {
          const ns = speed > 5 && d >= 0.05
            ? { ...s, driveSec: s.driveSec + dt, movedKm: s.movedKm + d }
            : { ...s, stopSec: s.stopSec + dt };
          localStorage.setItem(storageKey, JSON.stringify(ns));
          return ns;
        });
      }
    }
  }, [pos, fallbackPos, routeInfo, day, storageKey]);

  useEffect(() => { onKmDone?.(arrived ? day?.distance_km ?? null : kmDone); }, [kmDone, arrived, day?.distance_km, onKmDone]);

  if (!day || day.type === "stay") return null;

  const setArrived = async (val) => {
    try {
      await api.post(`/trips/${trip.id}/days/${day.day_number}/arrived`, { arrived: val });
      toast.success(val ? "Aangekomen! Dag afgevinkt — je volgers zien het ook 🏁" : "Aankomst ongedaan gemaakt");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const dayKm = day.distance_km || 1;
  const shownKm = arrived ? dayKm : kmDone;
  const pct = shownKm != null ? Math.min(Math.round((shownKm / dayKm) * 100), 100) : null;
  const remainingKm = shownKm != null ? Math.max(dayKm - shownKm, 0) : null;
  const chargeRemaining = (day.charging_stops || []).filter((cs) => shownKm == null || (cs.km_from_day_start || 0) > shownKm).reduce((a, cs) => a + (cs.charge_minutes || 0), 0);
  const avgSpeed = stats.driveSec > 300 ? stats.movedKm / (stats.driveSec / 3600) : null;
  const plannedSpeed = (day.drive_time_hours || 0) > 0 ? dayKm / day.drive_time_hours : null;
  const liveEta = avgSpeed > 10;
  const speedForEta = liveEta ? avgSpeed : plannedSpeed;
  const etaMin = !arrived && speedForEta > 0 ? ((remainingKm ?? dayKm) / speedForEta) * 60 + chargeRemaining : null;
  const eta = etaMin != null ? new Date(Date.now() + etaMin * 60000) : null;

  const plannedTotalMin = (day.drive_time_hours || 0) * 60 + (day.charging_stops || []).reduce((a, cs) => a + (cs.charge_minutes || 0), 0);
  const elapsedMin = (stats.driveSec + stats.stopSec) / 60;
  const expectedMin = pct != null ? (pct / 100) * plannedTotalMin : null;
  const deltaMin = expectedMin != null && elapsedMin > 5 && (shownKm ?? 0) >= 1 ? Math.round(elapsedMin - expectedMin) : null;

  return (
    <div className="bg-card rounded-2xl p-5 mb-6" data-testid="live-progress-card">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2">
          <Gauge className="w-4 h-4 text-[#52B788]" /> Voortgang vandaag
        </p>
        {deltaMin != null && (
          <span data-testid="schedule-status" className={`text-xs font-bold px-3 py-1 rounded-full ${deltaMin <= 5 ? "bg-[#52B788]/15 text-[#52B788]" : "bg-amber-500/15 text-amber-500"}`}>
            {deltaMin <= 5 ? (deltaMin < -5 ? `${Math.abs(deltaMin)} min voor op schema` : "Op schema") : `${deltaMin} min achter op schema`}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
          <div data-testid="progress-bar" className="h-full bg-[#52B788] rounded-full transition-[width] duration-1000" style={{ width: `${pct ?? 0}%` }} />
        </div>
        <span data-testid="progress-percent" className="font-mono font-bold text-lg text-[#52B788] shrink-0">{pct != null ? `${pct}%` : "—"}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <div>
          <p className="text-[10px] tracking-wider uppercase text-muted-foreground flex items-center justify-center gap-1"><Timer className="w-3 h-3" /> Gereden</p>
          <p data-testid="drive-time" className="font-mono font-bold text-sm mt-0.5">{fmtDur(stats.driveSec / 60)}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-wider uppercase text-muted-foreground flex items-center justify-center gap-1"><Coffee className="w-3 h-3" /> Gestopt</p>
          <p data-testid="stop-time" className="font-mono font-bold text-sm mt-0.5">{fmtDur(stats.stopSec / 60)}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-wider uppercase text-muted-foreground">Nog te gaan</p>
          <p data-testid="remaining-km" className="font-mono font-bold text-sm mt-0.5">{remainingKm != null ? `${Math.round(remainingKm)} km` : "—"}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-wider uppercase text-muted-foreground flex items-center justify-center gap-1">
            <Flag className="w-3 h-3" /> Aankomst
            {eta && (
              <button type="button" data-testid="eta-info-button" onClick={() => setEtaInfoOpen((o) => !o)}
                title="Hoe wordt de aankomsttijd berekend?" aria-label="Uitleg aankomsttijd"
                className={`p-0.5 rounded-full transition-[color,background-color] duration-150 ${etaInfoOpen ? "text-[#52B788]" : "text-muted-foreground hover:text-foreground"}`}>
                <Info className="w-3 h-3" />
              </button>
            )}
          </p>
          <p data-testid="eta-time" className="font-mono font-bold text-sm mt-0.5">{eta ? `${liveEta ? "" : "~"}${eta.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}` : "—"}</p>
        </div>
      </div>
      {etaInfoOpen && eta && (
        <div data-testid="eta-info-panel" className="mt-3 bg-secondary/60 rounded-xl p-3 text-left">
          <p className="text-[11px] font-bold mb-1 flex items-center gap-1.5"><Info className="w-3.5 h-3.5 text-[#52B788]" /> Zo berekenen we je aankomsttijd</p>
          <ul className="text-[11px] text-muted-foreground space-y-0.5 font-mono">
            <li data-testid="eta-info-drive">• nog {Math.round(remainingKm ?? dayKm)} km ÷ {Math.round(speedForEta)} km/u = {fmtDur(((remainingKm ?? dayKm) / speedForEta) * 60)} rijden</li>
            {chargeRemaining > 0 && <li data-testid="eta-info-charge">• + {chargeRemaining} min geplande laadstops die nog komen</li>}
            <li data-testid="eta-info-result">• nu + {fmtDur(etaMin)} = <span className="font-bold text-foreground">{eta.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}</span></li>
          </ul>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            {liveEta
              ? `De ${Math.round(speedForEta)} km/u is jouw werkelijke gemiddelde rijsnelheid van vandaag (stops tellen niet mee) — de tijd wordt live bijgesteld.`
              : `De ${Math.round(speedForEta)} km/u is de geplande snelheid van deze dag (afstand ÷ geplande rijtijd). Het ~-teken verdwijnt zodra je ±5 min gereden hebt: dan rekenen we met je echte gemiddelde snelheid.`}
          </p>
        </div>
      )}
      {!arrived && deltaMin != null && deltaMin >= 45 && remainingKm != null && remainingKm >= 60 && (
        <DelayCoach trip={trip} day={day} deltaMin={deltaMin} kmDone={shownKm} remainingKm={remainingKm} onRefresh={onRefresh} />
      )}
      {!arrived && shownKm != null && shownKm >= 1 && (
        <BatteryCoach trip={trip} day={day} kmDone={shownKm} movedKm={stats.movedKm} onRefresh={onRefresh} />
      )}
      {arrived ? (
        <div className="flex items-center justify-between gap-2 mt-4 bg-[#52B788]/10 rounded-xl px-4 py-3" data-testid="arrived-banner">
          <p className="text-sm font-bold text-[#52B788] flex items-center gap-2">
            <PartyPopper className="w-4 h-4" /> Aangekomen om {new Date(day.arrived_at).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <button data-testid="arrived-undo-button" onClick={() => setArrived(false)}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1 transition-[color] duration-200">
            <Undo2 className="w-3.5 h-3.5" /> Toch niet
          </button>
        </div>
      ) : (
        <>
          {day.date && day.date <= localToday() && (
            <button data-testid="arrived-button" onClick={() => setArrived(true)}
              className="w-full mt-4 rounded-full py-3 bg-[#52B788] hover:bg-[#3f9d73] text-[#0F172A] font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-[transform,background-color] duration-200">
              <Flag className="w-4 h-4" /> Wij zijn er! — dag afvinken
            </button>
          )}
          {!pos && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="live-progress-gps-hint">
              {fallbackPos && kmDone != null
                ? "Geen live GPS-signaal — voortgang gebaseerd op je laatst bekende positie."
                : "Wacht op GPS-signaal — zet de kaart op \"Live\" en sta locatietoegang toe."}
            </p>
          )}
        </>
      )}
    </div>
  );
};
